/**
 * The signed-in user's outstanding items — from `GET /api/me/action-items`.
 * ---------------------------------------------------------------------------
 * One request and one cache for the whole app, at module level, the same way
 * useLandingProfile shares its answer: the bell, and anything else that shows
 * a count, subscribe to the same store rather than each fetching.
 *
 * Freshness, without hammering the server (every open tab of every user runs
 * this):
 *   - polls every ACTION_ITEMS_POLL_MS, and only while the tab is visible — a
 *     background tab asks nothing;
 *   - refetches when the window regains focus, when the tab becomes visible,
 *     and on the app's `navigate-home` event — but at most once per
 *     REFETCH_THROTTLE_MS, because those events arrive in bursts;
 *   - after a failure, backs off to FAILURE_BACKOFF_MS until one succeeds;
 *   - `refresh()` is the explicit retry, and bypasses the throttle.
 *
 * Fails soft: a failed request keeps the last good data and sets `error`. A
 * count that disappears on a network blip would read as "nothing to do".
 *
 * Keyed on the USER, not the token. A JWT refresh mints a new token for the
 * same person, and resetting on that would blank the bell for no reason — the
 * new token is simply used for the next request. A different user resets the
 * store BEFORE anything is shown, and a response for an earlier user is
 * discarded, so signing out and in as somebody else can never show the
 * previous person's items.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import axios from 'axios';

export interface ActionItem {
  id: string;
  title: string;
  subtitle?: string;
  createdAt?: string;
  dueAt?: string;
  /** In-app relative path. Treat as untrusted: pass through safeContentHref. */
  href: string;
  severity?: 'normal' | 'warning' | 'overdue';
}

export interface ActionSection {
  key: string;
  label: string;
  kind: 'approval' | 'todo';
  /** The full count; `items` holds at most five. */
  count: number;
  oldestAt?: string;
  /** The newest item in the WHOLE section, not just the five sent. Optional. */
  newestAt?: string;
  items: ActionItem[];
  /** The server could not build this section. It arrives with no items. */
  error?: true;
}

export interface ActionItemsData {
  total: number;
  generatedAt: string;
  sections: ActionSection[];
}

export interface ActionItemsState {
  data: ActionItemsData | null;
  /** True until the first response (or failure) for the current user. */
  loading: boolean;
  /** The most recent request failed; `data` is the last good one, if any. */
  error: boolean;
}

export const ACTION_ITEMS_POLL_MS = 180_000;
export const REFETCH_THROTTLE_MS = 30_000;
export const FAILURE_BACKOFF_MS = 300_000;
const ENDPOINT = '/api/me/action-items';

/* ── the shared store ─────────────────────────────────────────────────── */

/** Whose items the store holds. */
let owner: string | null = null;
/** The token requests are sent with; swapped in place on a JWT refresh. */
let token: string | null = null;
let state: ActionItemsState = { data: null, loading: false, error: false };
let inflight: Promise<void> | null = null;
let lastFetchAt = 0;
const listeners = new Set<() => void>();

function setState(next: ActionItemsState): void {
  state = next;
  listeners.forEach(l => l());
}

function asData(raw: unknown): ActionItemsData | null {
  const d = (raw as { data?: Partial<ActionItemsData> } | null)?.data;
  if (!d || !Array.isArray(d.sections)) return null;
  return {
    total: Number(d.total) || 0,
    generatedAt: String(d.generatedAt || ''),
    sections: d.sections.map(s => ({ ...s, items: Array.isArray(s.items) ? s.items : [] })),
  };
}

/**
 * Fetch now for the current user. Concurrent callers share one request.
 * Never rejects. Schedules the next poll when it settles.
 */
export function fetchActionItems(): Promise<void> {
  if (!owner || !token) return Promise.resolve();
  if (inflight) return inflight;
  const forOwner = owner;
  lastFetchAt = Date.now();
  inflight = axios
    .get(ENDPOINT, { headers: { Authorization: `Bearer ${token}` } })
    .then(res => {
      if (owner !== forOwner) return; // signed out or switched user meanwhile
      const data = asData(res?.data);
      setState(data ? { data, loading: false, error: false } : { ...state, loading: false, error: true });
    })
    .catch(() => {
      if (owner !== forOwner) return;
      setState({ ...state, loading: false, error: true });
    })
    .finally(() => {
      if (owner !== forOwner) return;
      inflight = null;
      scheduleNext();
    });
  return inflight;
}

/** A refetch the app asked for implicitly (focus, visibility, navigation). */
function requestRefetch(): void {
  const since = Date.now() - lastFetchAt;
  if (since < REFETCH_THROTTLE_MS) return;
  if (state.error && since < FAILURE_BACKOFF_MS) return;
  void fetchActionItems();
}

/** A new user drops everything; a new token for the same user keeps it. */
function bindUser(nextOwner: string | null, nextToken: string | null): void {
  token = nextToken;
  if (nextOwner === owner) return;
  owner = nextOwner;
  inflight = null;
  lastFetchAt = 0;
  setState({ data: null, loading: !!nextOwner, error: false });
}

/** Forget everything. For tests, and for a sign-out that does not reload. */
export function resetActionItems(): void {
  owner = null;
  token = null;
  inflight = null;
  lastFetchAt = 0;
  state = { data: null, loading: false, error: false };
  listeners.forEach(l => l());
}

/* ── polling: one timer however many subscribers ─────────────────────── */

let subscribers = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

const isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

function scheduleNext(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  if (subscribers === 0) return;
  timer = setTimeout(onTimer, state.error ? FAILURE_BACKOFF_MS : ACTION_ITEMS_POLL_MS);
}

function onTimer(): void {
  timer = null;
  // A hidden tab skips this turn and checks again next period.
  if (isVisible()) void fetchActionItems();
  else scheduleNext();
}

const onRefetch = () => requestRefetch();
const onVisibility = () => {
  if (isVisible()) requestRefetch();
};

function startPolling(): void {
  subscribers += 1;
  if (subscribers > 1) return;
  window.addEventListener('focus', onRefetch);
  window.addEventListener('navigate-home', onRefetch);
  document.addEventListener('visibilitychange', onVisibility);
  if (!inflight) scheduleNext();
}

function stopPolling(): void {
  subscribers = Math.max(0, subscribers - 1);
  if (subscribers > 0) return;
  if (timer) clearTimeout(timer);
  timer = null;
  window.removeEventListener('focus', onRefetch);
  window.removeEventListener('navigate-home', onRefetch);
  document.removeEventListener('visibilitychange', onVisibility);
}

/* ── the hook ─────────────────────────────────────────────────────────── */

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const getSnapshot = () => state;

export interface UseActionItems extends ActionItemsState {
  refresh: () => Promise<void>;
}

/**
 * @param authToken the current JWT, sent with each request
 * @param userId    whose items these are. Without it the token stands in, which
 *                  is correct but resets the store on every JWT refresh.
 */
export function useActionItems(
  authToken: string | null | undefined,
  userId?: string | null
): UseActionItems {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const tok = authToken || null;
  const key = tok ? userId || tok : null;

  useEffect(() => {
    bindUser(key, tok);
    if (!key) return undefined;
    requestRefetch();
    startPolling();
    return stopPolling;
  }, [key, tok]);

  const refresh = useCallback(() => fetchActionItems(), []);

  // Never hand back another user's data, even for the render before the
  // effect above has reset the store.
  const mine = key !== null && owner === key;
  return mine ? { ...current, refresh } : { data: null, loading: !!key, error: false, refresh };
}
